import { format } from "date-fns";

import { Icon } from "semantic-ui-react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";
import { languageName } from "astrid-helpers/src/languages";

import truncateString from "../../../../ui/utils/truncateString";

import ProductionProgressBar from "../../../components/ProductionProgressBar/ProductionProgressBar";
import ProductionStatusLabel from "../../../components/ProductionStatus/ProductionStatusLabel";
import useProductionTagTranslations from "../../../hooks/useProductionTagTranslations";
import useProductionTypeTranslations from "../../../hooks/useProductionTypeTranslations";

export default function useProductionsColumns({ organization }) {
	const { t } = useTranslation();

	const tagTranslations = useProductionTagTranslations();
	const typeTranslations = useProductionTypeTranslations();

	return [
		{
			id: "status",
			Header: t("status"),
			collapsing: true,
			Cell: ({ value }) => <ProductionStatusLabel status={value} />,
		},
		{
			id: "title",
			Header: t("title"),
			collapsing: true,
			accessor: ({ title }) => truncateString(title, 30),
			Cell: ({ value, row }) => <Link to={`/production/${row.id}`}>{value}</Link>,
		},
		{
			id: "series",
			Header: t("serie", "Series"),
			accessor: ({ series, part }) => `${series || ""} ${part || ""}`,
		},
		{
			id: "productionType",
			Header: t("type", "Type"),
			accessor: ({ productionType }) => typeTranslations[productionType],
		},
		{
			id: "progressBar",
			Header: t("progress"),
			Cell: ({ value }) => {
				return <ProductionProgressBar {...value} />;
			},
		},
		{
			id: "noOfCorrections",
			Header: t("noOfCorrections"),
			hidden: organization?.type === organizationTypes.PUBLISHER,
			accessor: ({ progressBar }) => progressBar?.corrected?.progressText,
		},
		{
			id: "publisherName",
			Header: t("publisher", "Publisher"),
			hidden: organization?.type === organizationTypes.PUBLISHER,
			collapsing: true,
		},
		{
			id: "producerName",
			Header: t("producer", "Producer"),
			collapsing: true,
		},
		{
			id: "author",
			Header: t("author", "Author"),
			accessor: ({ author }) => truncateString(author?.join(",") || "", 30),
			collapsing: true,
		},
		{
			id: "isbn",
			Header: t("isbn", "ISBN"),
			collapsing: true,
			accessor: ({ isbn, ebookISBN, partISBN, isbns }) => {
				isbns = isbns?.length > 0 ? isbns : [isbn, ebookISBN, ...(partISBN || [])].filter(Boolean);

				return isbns.length > 1 ? `${isbns[0]} (+${isbns.length - 1})` : isbns[0];
			},
		},
		{
			id: "productionTags",
			Header: t("tags", "Tags"),
			accessor: ({ productionTags }) => productionTags?.map((tag) => tagTranslations[tag]).join(", "),
		},
		{
			id: "language",
			Header: t("language", "Language"),
			collapsing: true,
			accessor: ({ language }) => languageName(language, false),
		},
		{
			id: "publisherManagerNames",
			Header: t("publisherManager"),
			accessor: ({ publisherManagerNames }) => publisherManagerNames?.join(", "),
		},
		{
			id: "readerNames",
			Header: t("narrator"),
			accessor: ({ readerNames }) => readerNames?.join(", "),
		},
		{
			id: "voiceNames",
			Header: t("voices", "Voices"),
			accessor: ({ voiceNames }) => voiceNames?.join(", "),
		},
		{
			id: "editorNames",
			Header: t("editor"),
			hidden: organization?.type === organizationTypes.PUBLISHER,
			accessor: ({ editorNames }) => editorNames?.join(", "),
		},
		{
			id: "prooferNames",
			Header: t("proofer"),
			hidden: organization?.type === organizationTypes.PUBLISHER,
			accessor: ({ prooferNames }) => prooferNames?.join(", "),
		},
		{
			id: "managerNames",
			Header: t("productionManager"),
			accessor: ({ managerNames }) => managerNames?.join(", "),
		},
		{
			id: "subcontractorName",
			Header: t("subcontractor"),
		},
		{
			id: "duration",
			Header: t("duration"),
			collapsing: true,
			accessor: ({ duration }) =>
				duration?.time && duration?.isEstimated ? `${duration.time} *` : duration?.time,
		},
		{
			id: "pages",
			Header: t("pages", "Pages"),
			collapsing: true,
		},
		{
			id: "doneDate",
			Header: t("doneDate"),
			collapsing: true,
			accessor: ({ firstDoneDate_timestamp }) =>
				firstDoneDate_timestamp && format(new Date(firstDoneDate_timestamp * 1000), "yyyy-MM-dd"),
		},
		{
			id: "created",
			Header: t("created"),
			collapsing: true,
			accessor: ({ created_timestamp }) =>
				created_timestamp && format(new Date(created_timestamp * 1000), "yyyy-MM-dd"),
		},
		{
			id: "updated",
			Header: t("updated"),
			collapsing: true,
			accessor: ({ updated_timestamp }) =>
				updated_timestamp && format(new Date(updated_timestamp * 1000), "yyyy-MM-dd"),
		},
		{
			id: "scriptDate",
			Header: t("script"),
			collapsing: true,
			Cell: ({ value }) => {
				return (
					<p style={{ display: "flex", alignItems: "baseline" }}>
						<span style={{ whiteSpace: "nowrap" }}>{value?.date}</span>
						{value?.date && value?.isUploaded && (
							<Icon style={{ marginLeft: "5px" }} name="check" color="green" />
						)}
					</p>
				);
			},
		},
		{
			id: "productionDate",
			Header: t("prodDate"),
			hidden: organization?.type === organizationTypes.PUBLISHER,
			collapsing: true,
			accessor: ({ productionDate_timestamp }) =>
				productionDate_timestamp && format(new Date(productionDate_timestamp * 1000), "yyyy-MM-dd"),
		},
		{
			id: "deliveryDate",
			Header: t("deliveryDate"),
			collapsing: true,
			accessor: ({ deliveryDate_timestamp }) =>
				deliveryDate_timestamp && format(new Date(deliveryDate_timestamp * 1000), "yyyy-MM-dd"),
		},
	];
}
